import { Injectable } from '@angular/core';
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { GroupedCubesSearchRequirements } from '../model/vo/grouped-cubes-search-requirements';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { lastValueFrom } from 'rxjs';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { AccessoryService } from '@article/service/accessory.service';
import { PropertyCreator } from '@article/util/property-creator';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { ArticleTechnicalNumberValueVO, ArticleTechnicalStringValueVO, ArticleWithTechnicalPropertiesDTO } from '@article/properties/technical/technical-requirements';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { allValuesSet, ArrayUtils } from '@shared/util/array-utils';
import { AccessorySearchResultVO } from '../components/article/model/vo/accessory-search-result.vo';
import { ArticleNumberUtils } from '@article/util/article-number-utils';
import { AddArticleService } from '@configurator/project/add-article.service';
import { ConnectionLevelUtils } from '../cae/util/connection-level-utils';
import { CompositeArticleContainers } from '@configuration/assistants/mounting/mounting-rail/mounting-step-creator/composite-article-containers';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { Container } from '@3d-scene/model3d/container';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { SuitableConnectionAreasVO } from '../components/article/model/vo/suitable-connection-areas.vo';

interface CrossSectionChunk {
  crossSection: number;
  containers: Container[];
}

@Injectable({
    providedIn: 'root'
})
export class JumperService {

  constructor(private readonly articleDataService: ArticleDataService,
              private readonly containerService: ContainerService,
              private readonly accessoryService: AccessoryService,
              private readonly propertyCreator: PropertyCreator,
              private readonly addArticleService: AddArticleService,
              private readonly compositeArticleUtils: CompositeArticleUtils,
  ) {
  }

  public async addJumperArticleToArticles(articleGuids: string[], article: ArticleVO, level: number): Promise<boolean> {
    const compositeArticleContainers = new CompositeArticleContainers(this.containerService, this.compositeArticleUtils);
    const unconnectedContainers = this.getUnconnectedSortedContainers(articleGuids, compositeArticleContainers, level);
    const jumperConnectionsForContainers = this.getJumperConnectionsForContainers(unconnectedContainers, level);
    const givingAreas = ArticleUtils.givingAreas(article);
    const connectionLines = this.getConnectionLines(jumperConnectionsForContainers, givingAreas.length).at(0);
    if (connectionLines?.length === givingAreas.length) {
      const suitableConnection: SuitableConnectionAreasVO = SuitableConnectionAreasVO.create(article, givingAreas);
      const articleContainers = await this.addArticleService.addArticleToConnections(suitableConnection, connectionLines);
      return articleContainers.length > 0;
    }
    return false;
  }

  public async addJumpersToArticles(articleGuids: string[], level: number): Promise<boolean> {
    const compositeArticleContainers = new CompositeArticleContainers(this.containerService, this.compositeArticleUtils);
    const unconnectedContainers = this.getUnconnectedSortedContainers(articleGuids, compositeArticleContainers, level);
    const crossSectionRangeChunks = await this.getCrossSectionRangeChunks(unconnectedContainers);
    const success = await this.addReducingJumpersToArticles(crossSectionRangeChunks, compositeArticleContainers, level);
    if (!success) {
      return false;
    }
    return await this.addOtherJumpersToArticles(crossSectionRangeChunks, compositeArticleContainers, level);
  }

  private getUnconnectedSortedContainers(articleGuids: string[], compositeArticleContainers: CompositeArticleContainers, level: number): Container[] {
    const containers = articleGuids
      .map((articleGuid) => compositeArticleContainers.findContainer(articleGuid))
      .filter((container): container is Container => !!container);
    const containersWithJumperConnections = this.getArticleContainersWithJumperConnections(containers);
    const sortedArticleContainers = [...containersWithJumperConnections].sort((a, b) => b.position.x - a.position.x);
    const unconnectedContainers = this.getUnconnectedContainers(sortedArticleContainers, compositeArticleContainers, level);
    return unconnectedContainers;
  }

  private getArticleContainersWithJumperConnections(containers: Container[]): Container[] {
    return containers.filter(container => container.connections.some(JumperService.isJumperConnection));
  }

  private async getCrossSectionRangeForArticleContainers(containers: Container[]) {
    const compositeOrArticleIds = containers.map(container => container.article.identity);
    const articleInfos = await lastValueFrom(this.articleDataService.getArticleInfos(compositeOrArticleIds, [ArticleTechnicalInfoPropertyDef.NOMINAL_CROSS_SECTION]));
    const map = new Map<number, ArticleWithTechnicalPropertiesDTO>();
    for (const articleInfo of articleInfos) {
      map.set(articleInfo.articleId, articleInfo);
    }
    return map;
  }

  private static isJumperConnection(connection: Connection) {
    if (connection.metaElementVO instanceof ConnectionAreaVO) {
      return connection.metaElementVO.connectionRequirements
        .some(requirement =>
          requirement.name === 'Typ'
          && requirement.values
            .some(value => value.stringValue === ArticleConnectionPropertyValue.BRIDGE_SHAFT_CONNECTION_TYPE
              || value.stringValue === ArticleConnectionPropertyValue.BRIDGE_SLOT_CONNECTION_TYPE));
    }
  }

  private async addReducingJumpersToArticles(crossSectionRangeChunks: CrossSectionChunk[], compositeArticleContainers: CompositeArticleContainers, level: number): Promise<boolean> {
    for (let i = 0; i < crossSectionRangeChunks.length - 1; i++) {
      const predecessor = crossSectionRangeChunks[i].containers.at(-1);
      const successor = crossSectionRangeChunks[i + 1].containers[0];
      if (predecessor && successor) {
        const success = await this.addReducingJumpers(predecessor, successor, level);
        if (!success) {
          const success = await this.addOtherJumpers([predecessor, successor], compositeArticleContainers, level);
          if (!success) {
            return false;
          }
        }
      }
    }
    return true;
  }

  private async addReducingJumpers(predecessor: Container, successor: Container, level: number): Promise<boolean> {
    const jumperConnectionsForContainers = this.getJumperConnectionsForContainers([predecessor, successor], level);
    const connectionLines = this.getConnectionLines(jumperConnectionsForContainers, 2);
    for (const connectionLine of connectionLines) {
      const accessorySearchResult = await this.getReducingJumperAccessory(connectionLine);
      if (await this.addJumper(accessorySearchResult, connectionLine)) {
        return true;
      }
    }
    return false;
  }

  private async getReducingJumperAccessory(connections: Connection[]): Promise<AccessorySearchResultVO> {
    const accessorySearchResult = await this.getJumperAccessoryForConnections(connections);
    const accessoryArticleIds = accessorySearchResult.articleInfoConnectionSet.map(set => set.articleWithTechnicalProperties.articleId);
    const articleTechnicalSearchResults = await lastValueFrom(this.articleDataService.getArticleInfos(accessoryArticleIds, [ArticleTechnicalInfoPropertyDef.TYPE_OF_MODULE]));
    const articleIdToTechnicalSearchResult = ArrayUtils.toMap(articleTechnicalSearchResults, result => result.articleId);
    accessorySearchResult.articleInfoConnectionSet = accessorySearchResult.articleInfoConnectionSet.filter(set => {
      const articleTechnicalSearchResult = articleIdToTechnicalSearchResult.get(set.articleWithTechnicalProperties.articleId);
      return (articleTechnicalSearchResult?.properties[ArticleTechnicalInfoPropertyDef.TYPE_OF_MODULE] as ArticleTechnicalStringValueVO)?.englishValue === 'Step-down jumper';
    });
    return accessorySearchResult;
  }

  private getJumperConnectionsForContainers(containers: Container[], level: number): (Connection | undefined)[][] {
    return containers.map((container) =>
      ConnectionInfoUtils.sortConnections(
        container.connections
        .filter(JumperService.isJumperConnection)
        .filter((connection) => JumperService.isCalculatedLevel(connection, level))
      ).map((jumperConnection) => ConnectionInfoUtils.isFreeConnection(jumperConnection, true) ? jumperConnection : undefined),
    );
  }

  private getJumperAccessoryForConnections(connections: Connection[]) {
    const cubesSearchRequirements: ConnectionCubeSearchRequirements[] = this.propertyCreator.createCubesSearchRequirementsByDefault(connections);
    const groupedCubesSearchRequirements: GroupedCubesSearchRequirements[] = [new GroupedCubesSearchRequirements(cubesSearchRequirements)];
    const searchQueryBuilder = this.accessoryService.createSearchQueryBuilderForGroup(groupedCubesSearchRequirements, [ArticleTypeConstants.TYPE_BRIDGE]);
    return lastValueFrom(this.accessoryService.searchAccessoriesForBuilder(searchQueryBuilder, []));
  }

  private async addOtherJumpersToArticles(crossSectionRangeChunks: CrossSectionChunk[], compositeArticleContainers: CompositeArticleContainers, level: number): Promise<boolean> {
    for (const chunk of crossSectionRangeChunks) {
      if (chunk.containers.length > 1) {
        const success = await this.addOtherJumpers(chunk.containers, compositeArticleContainers, level);
        if (!success) {
          return false;
        }
      }
    }
    return true;
  }

  private getCrossSection(container: Container, articleInfoMap: Map<number, ArticleWithTechnicalPropertiesDTO>): number {
    const compositeOrArticleId = container.article.identity;
    const properties = articleInfoMap.get(compositeOrArticleId);
    if (properties) {
      const crossSectionProperty = properties.properties[ArticleTechnicalInfoPropertyDef.NOMINAL_CROSS_SECTION] as ArticleTechnicalNumberValueVO;
      return crossSectionProperty.value;
    }
    return Number.NaN;
  }

  private async addOtherJumpers(containers: readonly Container[], compositeArticleContainers: CompositeArticleContainers, level: number): Promise<boolean> {
    const unconnectedContainers = this.getUnconnectedContainers(containers, compositeArticleContainers, level);
    if (unconnectedContainers.length === 0) {
      return true;
    }
    const jumperConnectionsForContainers = this.getJumperConnectionsForContainers(unconnectedContainers, level);
    const accessorySearchResult = await this.getOtherJumperAccessory(jumperConnectionsForContainers.flat().filter((connection) => !!connection));
    const connectionLines = this.getConnectionLines(jumperConnectionsForContainers, Math.max(...accessorySearchResult.articleInfoConnectionSet.flatMap((set) => set.connectionSets.map((s) => s.connectionNames.length))));
    for (const connectionLine of connectionLines) {
      if (connectionLine && await this.addJumper(accessorySearchResult, connectionLine)) {
        compositeArticleContainers.reload();
        containers = containers
          .map((container) => compositeArticleContainers.findContainer(container.articleGUID))
          .filter((container): container is Container => !!container);
        return await this.addOtherJumpers(containers, compositeArticleContainers, level);
      }
    }
    return false;
  }

  private async getOtherJumperAccessory(connections: Connection[]) {
    const accessorySearchResult = await this.getJumperAccessoryForConnections(connections);
    const accessoryArticleIds = accessorySearchResult.articleInfoConnectionSet.map(set => set.articleWithTechnicalProperties.articleId);
    const articleTechnicalSearchResults = await lastValueFrom(this.articleDataService.getArticleInfos(accessoryArticleIds, [ArticleTechnicalInfoPropertyDef.TYPE_OF_MODULE]));
    const articleIdToTechnicalSearchResult = ArrayUtils.toMap(articleTechnicalSearchResults, result => result.articleId);
    accessorySearchResult.articleInfoConnectionSet =
      accessorySearchResult.articleInfoConnectionSet
        .filter(set => {
          const articleTechnicalSearchResult = articleIdToTechnicalSearchResult.get(set.articleWithTechnicalProperties.articleId);
          const isJumper = (articleTechnicalSearchResult?.properties[ArticleTechnicalInfoPropertyDef.TYPE_OF_MODULE] as ArticleTechnicalStringValueVO)?.englishValue === 'Jumper';
          return isJumper;
        })
        .sort((set1, set2) => {
          const maxConnectionsSet1 = Math.max(...set1.connectionSets.map((set) => set.connectionNames.length));
          const maxConnectionsSet2 = Math.max(...set2.connectionSets.map((set) => set.connectionNames.length));
          if (maxConnectionsSet1 === maxConnectionsSet2) {
            return ArticleNumberUtils.compareArticleNumbersDesc(set1.articleWithTechnicalProperties.articleNo, set2.articleWithTechnicalProperties.articleNo);
          } else {
            return maxConnectionsSet2 - maxConnectionsSet1;
          }
        });
    return accessorySearchResult;
  }

  private async getCrossSectionRangeChunks(containers: Container[]): Promise<CrossSectionChunk[]> {
    const articleInfoMap = await this.getCrossSectionRangeForArticleContainers(containers);
    const chunks: CrossSectionChunk[] = [];
    for (const container of containers) {
      const lastChunk = chunks.at(-1);
      const crossSection: number = this.getCrossSection(container, articleInfoMap);
      if (lastChunk?.crossSection === crossSection) {
        lastChunk.containers.push(container);
      } else {
        chunks.push({crossSection, containers: [container]});
      }
    }
    return chunks;
  }

  private async addJumper(accessorySearchResult: AccessorySearchResultVO, connections: Connection[]): Promise<boolean> {

    for (const articleInfoConnectionSet of accessorySearchResult.articleInfoConnectionSet) {
      for (const connectionSet of articleInfoConnectionSet.connectionSets) {
        if (connectionSet.connectionNames.length === connections.length) {
          const articles = await lastValueFrom(this.articleDataService.getLatestArticlesByNoObservable([articleInfoConnectionSet.articleWithTechnicalProperties.articleNo]));
          const givingConnectionAreas: ConnectionAreaVO[] = ArticleUtils.givingAreas(articles[0]).filter((area) => connectionSet.connectionNames.includes(area.name));
          const suitableConnectionAreas: SuitableConnectionAreasVO = SuitableConnectionAreasVO.create(articles[0], givingConnectionAreas);
          const articleContainers = await this.addArticleService.addArticleToConnections(suitableConnectionAreas, connections);
          if (articleContainers.length > 0) {
            return true;
          }
        }
      }
    }

    return false;
  }

  private static isCalculatedLevel(connection: Connection, level: number) {
    return ConnectionLevelUtils.getConnectionLevelByProperty(connection.metaElementVO) === level;
  }

  private getUnconnectedContainers(containers: readonly Container[], compositeArticleContainers: CompositeArticleContainers, level: number) {
    const unconnectedContainers: Container[] = [];
    for (let i = 1; i < containers.length; i++) {
      const previousContainer = containers[i - 1];
      const currentContainer = containers[i];
      const previousChildren = compositeArticleContainers.getChildren(previousContainer, { level });
      const currenChildren = compositeArticleContainers.getChildren(currentContainer, { level });
      const previousBridges = previousChildren.filter((child) => child.article.type.id === ArticleTypeIds.BRIDGE);
      const currentBridges = new Set(currenChildren.filter((child) => child.article.type.id === ArticleTypeIds.BRIDGE));
      if (previousBridges.some((previousBridge) => currentBridges.has(previousBridge))) {
        if (unconnectedContainers.length > 0) {
          return unconnectedContainers;
        }
        continue;
      }
      if (unconnectedContainers.length === 0) {
        unconnectedContainers.push(previousContainer);
      }
      unconnectedContainers.push(currentContainer);
    }
    return unconnectedContainers;
  }

  private getConnectionLines(connectionsOfContainers: (Connection | undefined)[][], maxConnections: number): Connection[][] {

    const connectionLines = ArrayUtils.transpose(connectionsOfContainers);

    // Find the ones from start to finish
    const wholeConnections = connectionLines.filter((line) => line.at(0) !== undefined && line.at(-1) !== undefined);
    if (wholeConnections.length > 0 && wholeConnections[0].length <= maxConnections) {
      return wholeConnections.filter(allValuesSet);
    }

    // Find the ones in between
    const betweenConnections = connectionLines.filter((line) => line.at(0) === undefined && line.at(-1) === undefined);
    if (betweenConnections.length > 0 && betweenConnections[0].length - 2 <= maxConnections) {
      return betweenConnections.map((line) => line.slice(1, -1)).filter(allValuesSet);
    }

    // Find the one to start
    const startingConnection = connectionLines.find((line) => line.at(0) !== undefined);
    if (startingConnection && startingConnection.at(-1) === undefined) {
      return [startingConnection.slice(0, -1).slice(0, maxConnections)].filter(allValuesSet);
    }
    if (startingConnection && connectionLines.some((line) => line.at(-1) === undefined)) {
      return [startingConnection.slice(0, -2).slice(0, maxConnections)].filter(allValuesSet);
    }
    if (startingConnection) {
      return [startingConnection.slice(0, maxConnections)].filter(allValuesSet);
    }

    return [];
  }
}
